import { useEffect } from 'react';
import { Control, FormProvider, useForm, useWatch } from 'react-hook-form';

import { Button } from '@creditornot/cb-button';
import { Modal } from '@creditornot/cbt-modal';
import { ModalFooter } from 'common/components/cookbook/Modals/ModalFooter';
import { ModalHeader } from 'common/components/cookbook/Modals/ModalHeader';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { AxiosDelistingRecommendationsError } from 'common/types';
import { CommentRow } from 'features/selection-recommendations/common/RecommendationActions/Reject/CommentRow';
import { DelistingRecommendationRejectionFormValues } from 'features/selection-recommendations/Recommendations/DelistingRecommendations/types';
import { downloadableReasons } from 'features/selection-recommendations/Recommendations/DelistingRecommendations/utils';

import { ReasonRow } from './ReasonRow';

type Props = {
  isOpen: boolean;
  rejectIsPending: boolean;
  onDismiss: () => void;
  onReject: (values: DelistingRecommendationRejectionFormValues) => void;
  selectedRecommendationCount: number;
  error?: AxiosDelistingRecommendationsError | null;
};

type RejectFormFooterProps = {
  rejectIsPending: boolean;
  handleDismiss: () => void;
  control: Control<DelistingRecommendationRejectionFormValues>;
};

const RejectFormFooter = ({ rejectIsPending, handleDismiss, control }: RejectFormFooterProps) => {
  const f = useFormatMessage();
  const reason = useWatch({ control, name: 'reason' });

  return (
    <>
      <Button variant="inverse" size="small" onClick={handleDismiss}>
        {f('common.cancel')}
      </Button>
      <Button
        variant="primary"
        size="small"
        type="submit"
        data-test-id="reject-recommendation-button"
        form="reject-form"
        disabled={!reason}
        loading={rejectIsPending}
      >
        {downloadableReasons.includes(reason)
          ? f('selection_recommendations.reject_download_csv')
          : f('selection_recommendations.reject')}
      </Button>
    </>
  );
};

export const RejectDelistingsForm = ({
  isOpen,
  rejectIsPending,
  selectedRecommendationCount,
  onDismiss,
  onReject,
  error,
}: Props) => {
  const f = useFormatMessage();

  const form = useForm<DelistingRecommendationRejectionFormValues>({
    mode: 'onChange',
    defaultValues: {
      reason: undefined,
      comment: undefined,
    },
  });

  const handleDismiss = () => {
    form.reset();
    onDismiss();
  };

  useEffect(() => {
    form.reset();
  }, [form.formState.isSubmitSuccessful, form]);

  const handleSubmit = (values: DelistingRecommendationRejectionFormValues) => {
    onReject({
      reason: values.reason,
      comment: values.comment?.trim(),
    });
  };

  return (
    <Modal
      data-test-id="reject-modal"
      sizePreset="centered:large"
      open={isOpen}
      onDismiss={handleDismiss}
      title={f('selection_recommendations.reject_recommendation')}
      HeaderComponent={({ onCloseClick, ...rest }) => (
        <ModalHeader onDismiss={onCloseClick} {...rest}>
          {f('selection_recommendations.reject_recommendation')}
        </ModalHeader>
      )}
      footer={
        <RejectFormFooter
          rejectIsPending={rejectIsPending}
          handleDismiss={handleDismiss}
          control={form.control}
        />
      }
      FooterComponent={(props) => <ModalFooter error={error} {...props} />}
    >
      <FormProvider {...form}>
        <form
          id="reject-form"
          onSubmit={form.handleSubmit(handleSubmit)}
          data-test-id="reject-form"
        >
          <div>
            {f('selection_recommendations.reject_delisting_recommendation_description', {
              count: selectedRecommendationCount,
            })}
          </div>
          <ReasonRow />
          <CommentRow />
        </form>
      </FormProvider>
    </Modal>
  );
};
